import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  AdminEmptyState,
  AdminErrorBanner,
  AdminPagination,
} from './AdminTableChrome'
import {
  ADMIN_PAGE_SIZE,
  adminKeys,
  formatDateTimeAr,
  type AdminSession,
  type AdminSessionListResponse,
} from './types'

/**
 * Maps a backend `GameState` enum name to its `admin`-namespace state-label key.
 */
function stateLabelKey(
  state: AdminSession['state'],
):
  | 'sessionStateLobby'
  | 'sessionStateInQuestion'
  | 'sessionStateShowingResults'
  | 'sessionStateShowingLeaderboard'
  | 'sessionStateFinal' {
  switch (state) {
    case 'Lobby':
      return 'sessionStateLobby'
    case 'InQuestion':
      return 'sessionStateInQuestion'
    case 'ShowingResults':
      return 'sessionStateShowingResults'
    case 'ShowingLeaderboard':
      return 'sessionStateShowingLeaderboard'
    case 'Final':
      return 'sessionStateFinal'
  }
}

/**
 * Paginated session-history table (read-only oversight). Defaults to all
 * sessions (newest first), with a segmented toggle to narrow to currently-active
 * ones. Columns: PIN, state (localized, green+pulse for active / grey for
 * ended), opened by (host name over email), type (live/random), connected
 * players, created, ended. The query auto-refreshes every 10s only on the active
 * filter (a live view); history does not poll.
 */
export function AdminSessionTable() {
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')

  const [status, setStatus] = useState<'all' | 'active'>('all')
  const [page, setPage] = useState(1)

  const sessionsQuery = useQuery({
    queryKey: adminKeys.sessions({ status, page }),
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams()
      params.set('status', status)
      params.set('page', String(page))
      params.set('pageSize', String(ADMIN_PAGE_SIZE))
      const res = await fetch(`/api/v1/admin/sessions?${params.toString()}`, {
        credentials: 'include',
        signal,
      })
      if (!res.ok) {
        throw new Error(`Failed to load sessions: ${res.status}`)
      }
      return (await res.json()) as AdminSessionListResponse
    },
    // Only the active view is "live" — poll it; history does not reshuffle.
    refetchInterval: status === 'active' ? 10_000 : false,
  })

  const total = sessionsQuery.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / ADMIN_PAGE_SIZE))
  const sessions = sessionsQuery.data?.sessions ?? []

  const changeStatus = (next: 'all' | 'active') => {
    setStatus(next)
    setPage(1)
  }

  return (
    <div>
      <div className="mb-6">
        <div
          role="group"
          aria-label={t('sessionState')}
          className="inline-flex rounded-xl border border-brand-200 bg-white p-1"
          dir="rtl"
        >
          <SegmentButton
            active={status === 'all'}
            onClick={() => changeStatus('all')}
            label={t('sessionFilterAll')}
          />
          <SegmentButton
            active={status === 'active'}
            onClick={() => changeStatus('active')}
            label={t('sessionFilterActive')}
          />
        </div>
      </div>

      {sessionsQuery.isLoading ? <TableSkeleton /> : null}

      {sessionsQuery.isError ? <AdminErrorBanner message={t('loadError')} /> : null}

      {sessionsQuery.isSuccess && sessions.length === 0 ? (
        <AdminEmptyState message={status === 'active' ? t('noActiveSessions') : t('noSessions')} />
      ) : null}

      {sessionsQuery.isSuccess && sessions.length > 0 ? (
        <>
          {/* Desktop: semantic table. */}
          <div className="hidden overflow-hidden rounded-2xl border border-brand-100 bg-white md:block">
            <table className="w-full border-collapse text-sm" dir="rtl">
              <thead>
                <tr className="border-b border-brand-100 bg-brand-50/40 text-xs font-semibold uppercase tracking-wide text-brand-600">
                  <th scope="col" className="px-4 py-3 text-start">{t('sessionPin')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('sessionState')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('openedBy')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('sessionType')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('players')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('userColCreated')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('sessionEndedAt')}</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => (
                  <tr key={session.id} className="border-b border-brand-50 last:border-0 hover:bg-brand-50/30">
                    <td className="px-4 py-3 align-top font-semibold text-brand-900 tabular-nums">
                      <span dir="ltr">{session.pin}</span>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <StateBadge state={session.state} label={t(stateLabelKey(session.state))} />
                    </td>
                    <td className="px-4 py-3 align-top">
                      <HostCell session={session} />
                    </td>
                    <td className="px-4 py-3 align-top text-brand-700">
                      {session.isRandom ? t('sessionTypeRandom') : t('sessionTypeLive')}
                    </td>
                    <td className="px-4 py-3 align-top text-brand-700 tabular-nums">
                      <span dir="ltr">{session.playerCount}</span>
                    </td>
                    <td className="px-4 py-3 align-top text-brand-700">
                      <span dir="ltr">{formatDateTimeAr(session.createdAt)}</span>
                    </td>
                    <td className="px-4 py-3 align-top text-brand-700">
                      {session.endedAt !== null ? (
                        <span dir="ltr">{formatDateTimeAr(session.endedAt)}</span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: stacked label:value cards. */}
          <ul role="list" className="space-y-4 md:hidden">
            {sessions.map((session) => (
              <li
                key={session.id}
                className="rounded-2xl border border-brand-100 bg-white border-s-4 border-s-brand-500 ps-4 pe-4 py-4 shadow-sm"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold text-brand-900 tabular-nums" dir="ltr">{session.pin}</span>
                  <StateBadge state={session.state} label={t(stateLabelKey(session.state))} />
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <dt className="text-brand-500">{t('openedBy')}</dt>
                  <dd className="text-end"><HostCell session={session} /></dd>
                  <dt className="text-brand-500">{t('sessionType')}</dt>
                  <dd className="text-brand-800 text-end">
                    {session.isRandom ? t('sessionTypeRandom') : t('sessionTypeLive')}
                  </dd>
                  <dt className="text-brand-500">{t('players')}</dt>
                  <dd className="text-brand-800 text-end tabular-nums" dir="ltr">{session.playerCount}</dd>
                  <dt className="text-brand-500">{t('userColCreated')}</dt>
                  <dd className="text-brand-800 text-end" dir="ltr">{formatDateTimeAr(session.createdAt)}</dd>
                  <dt className="text-brand-500">{t('sessionEndedAt')}</dt>
                  <dd className="text-brand-800 text-end" dir="ltr">
                    {session.endedAt !== null ? formatDateTimeAr(session.endedAt) : '—'}
                  </dd>
                </dl>
              </li>
            ))}
          </ul>

          <AdminPagination
            page={page}
            pageCount={pageCount}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(pageCount, p + 1))}
          />
        </>
      ) : null}

      {sessionsQuery.isLoading ? (
        <span className="sr-only" role="status" aria-live="polite">
          {tCommon('loading')}
        </span>
      ) : null}
    </div>
  )
}

/**
 * Renders a session's host cell: display name (primary) over email (secondary,
 * `dir="ltr"`). A null host or an orphaned host (display name unresolved because
 * the user was deleted) both render as "deleted user" — sessions always have a
 * host, so a missing one is a data anomaly handled defensively.
 */
function HostCell({ session }: { session: AdminSession }) {
  const { t } = useTranslation('admin')

  if (session.hostDisplayName === null) {
    return <div className="text-brand-800">{t('quizHostUnknown')}</div>
  }
  return (
    <>
      <div className="text-brand-800">{session.hostDisplayName}</div>
      {session.hostEmail !== null ? (
        <div className="text-xs text-brand-600" dir="ltr">{session.hostEmail}</div>
      ) : null}
    </>
  )
}

/** A single segment in the all/active filter toggle. */
function SegmentButton({
  active,
  onClick,
  label,
}: {
  active: boolean
  onClick: () => void
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors ${
        active ? 'bg-brand-600 text-white' : 'text-brand-600 hover:bg-brand-50'
      }`}
    >
      {label}
    </button>
  )
}

/**
 * State badge. Active states (anything before `Final`) keep the live green
 * pulsing dot; `Final` is a neutral grey static badge (no pulse).
 */
function StateBadge({ state, label }: { state: AdminSession['state']; label: string }) {
  const isActive = state !== 'Final'
  if (!isActive) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-semibold text-brand-600">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-400" aria-hidden="true" />
        {label}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
      <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
      {label}
    </span>
  )
}

function TableSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-brand-100 bg-white" aria-hidden="true">
      <div className="space-y-px">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-4 animate-pulse">
            <div className="h-6 w-20 rounded bg-brand-50" />
            <div className="h-6 w-24 rounded bg-brand-50" />
            <div className="h-6 flex-1 rounded bg-brand-50" />
            <div className="h-6 w-16 rounded bg-brand-50" />
            <div className="h-6 w-12 rounded bg-brand-50" />
          </div>
        ))}
      </div>
    </div>
  )
}
